import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { homeForRole } from "@/lib/roles";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import OjentLogo from "@/components/OjentLogo";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    if (user.role === "admin" && user.restaurant && !user.restaurant.onboarding_complete) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to={homeForRole(user.role)} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role === "admin" && data.restaurant && !data.restaurant.onboarding_complete) {
        navigate("/onboarding");
      } else {
        navigate(homeForRole(data.role));
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[var(--kul-charcoal)]">
        <img src="https://images.unsplash.com/photo-1762087577613-978bf9066d39?w=1200&q=80"
          alt="restaurant" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--kul-charcoal)] via-transparent to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <OjentLogo markSize={44} variant="dark" />
          <div>
            <h1 className="font-display text-white text-4xl xl:text-5xl font-800 leading-tight tracking-tight">
              The Restaurant<br />Operating System.
            </h1>
            <p className="text-neutral-300 mt-4 max-w-md text-base leading-relaxed">
              Billing, orders, tables, inventory, CRM & analytics — one lightning-fast platform for cafés, cloud kitchens & fine dining.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-8">
            <OjentLogo markSize={36} />
          </div>
          <h2 className="font-display text-3xl font-800 tracking-tight">Welcome back</h2>
          <p className="text-neutral-500 mt-1 text-sm">Sign in to manage your restaurant.</p>

          <div className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email" placeholder="you@restaurant.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-[var(--kul-danger)]" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-11 bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </Button>
            <p className="text-sm text-center text-neutral-500">
              New restaurant? <Link to="/signup" className="text-[var(--kul-primary)] font-medium">Onboard here</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
